import HttpError from "./Utils/HttpError";
import { ValidationError } from "../Common/types";
import { HttpResponse } from "../Common/request";

type ErrorResponseBody = {
    error: {
        details: string | ValidationError[];
    };
};

type SimpleErrorResponseBody = {
    error: {
        details: string;
    };
};

type ValidationErrorResponseBody = {
    error: {
        details: ValidationError[];
    };
};

type SimpleErrorResponse = HttpResponse<SimpleErrorResponseBody>;
type ValidationErrorResponse = HttpResponse<ValidationErrorResponseBody>;

export default {
    throwSimpleError(res: HttpResponse<unknown>): void {
        if (this.isSimpleErrorKey(res)) {
            throw new HttpError(this.getSimpleErrorKey(res), res.statusCode, res.body);
        }
    },
    throwValidationErrors(res: HttpResponse<unknown>): void {
        if (this.isValidationErrorResponse(res)) {
            const error = new HttpError("flash_messages.invalid_form", res.statusCode, res.body);
            error.validationErrors = this.getValidationErrorsFromResponse(res);
            throw error;
        }
    },
    throwValidationErrorsAsText(res: HttpResponse<unknown>): void {
        if (this.isValidationErrorResponse(res)) {
            const error = new HttpError(this.getValidationErrorsFromResponseAsText(res), res.statusCode, res.body);
            error.alreadyTranslated = true;
            throw error;
        }
    },
    getErrorDetails(res: HttpResponse<unknown>): string | ValidationError[] | null {
        if (res.body && typeof res.body === "object" && "error" in res.body) {
            const body = res.body as ErrorResponseBody;
            if (body.error && body.error.details) {
                return body.error.details;
            }
        }
        return null;
    },
    /**
     * @deprecated Use throwValidationErrors.
     */
    isValidationErrorResponse(res: HttpResponse<unknown>): res is ValidationErrorResponse {
        if (res.statusCode !== 400) {
            return false;
        }
        if (!res.body || typeof res.body !== "object") {
            return false;
        }
        if (!("error" in res.body)) {
            return false;
        }
        const body = res.body as { error?: { details?: unknown } };
        if (!body.error?.details) {
            return false;
        }
        return Array.isArray(body.error.details);
    },
    /**
     * @deprecated Use throwSimpleError.
     */
    isSimpleErrorKey(res: HttpResponse<unknown>): res is SimpleErrorResponse {
        if (res.statusCode < 400) {
            return false;
        }
        if (!res.body || typeof res.body !== "object") {
            return false;
        }
        if (!("error" in res.body)) {
            return false;
        }
        const body = res.body as { error?: { details?: unknown } };
        if (!body.error?.details) {
            return false;
        }

        return typeof body.error.details === "string";
    },
    getValidationErrorsFromResponse(res: HttpResponse<unknown>): ValidationError[] {
        if (!this.isValidationErrorResponse(res)) {
            throw new HttpError("No validation errors available", res.statusCode, res.body);
        }

        return res.body.error.details;
    },
    getValidationErrorsFromResponseAsText(res: HttpResponse<unknown>): string {
        if (!this.isValidationErrorResponse(res)) {
            throw new HttpError("No validation errors available", res.statusCode, res.body);
        }
        const messages = res.body.error.details.map(errorDetail => Object.values(errorDetail.message).join(", "));
        return messages.join("\n");
    },
    getSimpleErrorKey(res: HttpResponse<unknown>): string {
        if (!this.isSimpleErrorKey(res)) {
            throw new HttpError("No simple error key available", res.statusCode, res.body);
        }

        return res.body.error.details;
    },
};
